import { supabase } from '../lib/supabase'
import { supportCenterAppointmentsService } from './supportCenterAppointments'
import {
  isAppointmentPastForAttendance,
  triagePastMessage,
} from './supportCenterAttendance'
import {
  assertAppointmentBelongsToTriageCase,
  resolveAppointmentsByTriageIds,
} from './supportCenterCaseTriage'
import { supportCenterFormsService } from './supportCenterForms'
import { supportCenterParametersService } from './supportCenterParameters'
import { peopleContactsService } from './peopleContacts'
import { nowTimestampSaoPaulo, timestampDayEnd, timestampDayStart } from '../utils/dateOnly'
import {
  isSupportCenterFormKind,
  resolveAssistanceFormKind,
  type SupportCenterFormKind,
} from '../types/supportCenterSpecialties'
import {
  TRIAGE_STATUS_LABELS,
  isReducedTriageForm,
  reducedTriageRequiredMessage,
  reducedTriageText,
  type SupportCenterAppointment,
  type SupportCenterDemandSelectionItem,
  SupportCenterDemandType,
  SupportCenterPeopleRef,
  SupportCenterTriage,
  SupportCenterTriageCancelInput,
  SupportCenterTriageCompleteInput,
  SupportCenterTriageFilters,
  SupportCenterTriageListItem,
  SupportCenterTriageJuridicalDetail,
  SupportCenterTriageObjectiveDetail,
  SupportCenterTriagePsychological,
  SupportCenterTriageRejectInput,
  SupportCenterTriageSaveFormInput,
  SupportCenterTriageStatus,
} from '../types/supportCenterTriage'

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map(String).filter((item) => item.trim().length > 0)
}

function mapPeople(row: unknown): SupportCenterAppointment['holder_people'] {
  if (!row || typeof row !== 'object') return null
  const people = row as Record<string, unknown>
  return {
    id: Number(people.id),
    name: (people.name as string | null) ?? null,
    tax_id_number: (people.tax_id_number as string | null) ?? null,
    captured_birth_date: (people.captured_birth_date as string | null) ?? null,
  }
}

function mapIntern(row: unknown): SupportCenterTriage['intern'] {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null
  const intern = row as Record<string, unknown>
  return {
    id: String(intern.id),
    name: (intern.name as string | null) ?? null,
  }
}

function mapProfessional(row: unknown): SupportCenterAppointment['professional'] {
  const raw = Array.isArray(row) ? row[0] : row
  if (!raw || typeof raw !== 'object') return null
  const profile = raw as Record<string, unknown>
  const id = String(profile.id ?? '')
  if (!id) return null
  return {
    id,
    name: (profile.name as string | null) ?? null,
    email: null,
  }
}

function normalizeDemandSelections(
  selections: SupportCenterDemandSelectionItem[]
): SupportCenterDemandSelectionItem[] {
  const unique = new Map<string, SupportCenterDemandSelectionItem>()
  for (const item of selections) {
    const demand_type_id = String(item.demand_type_id ?? '').trim()
    if (!demand_type_id) continue
    unique.set(demand_type_id, {
      demand_type_id,
      notes: String(item.notes ?? ''),
    })
  }
  return [...unique.values()]
}

function mapDemandTypes(raw: unknown): SupportCenterTriage['demand_types'] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const demand = row.demand_type
      if (!demand || typeof demand !== 'object') return null
      const demandRow = demand as Record<string, unknown>
      return {
        id: String(demandRow.id),
        name: String(demandRow.name ?? ''),
        requires_notes: Boolean(demandRow.requires_notes),
        notes: (row.notes as string | null) ?? null,
      }
    })
    .filter((item): item is NonNullable<SupportCenterTriage['demand_types']>[number] => !!item)
}

function mapPsychological(raw: unknown): SupportCenterTriagePsychological | null {
  const row = Array.isArray(raw) ? raw[0] : raw
  if (!row || typeof row !== 'object') return null
  const psy = row as Record<string, unknown>
  if (!psy.id) return null
  return {
    id: String(psy.id),
    suffering_intensity:
      (psy.suffering_intensity as SupportCenterTriagePsychological['suffering_intensity']) ?? null,
    demand_nature: (psy.demand_nature as SupportCenterTriagePsychological['demand_nature']) ?? null,
    suicidal_ideation:
      (psy.suicidal_ideation as SupportCenterTriagePsychological['suicidal_ideation']) ?? null,
    self_harm: (psy.self_harm as SupportCenterTriagePsychological['self_harm']) ?? null,
    aggression_risk:
      (psy.aggression_risk as SupportCenterTriagePsychological['aggression_risk']) ?? null,
    domestic_violence:
      (psy.domestic_violence as SupportCenterTriagePsychological['domestic_violence']) ?? null,
    observations: (psy.observations as string | null) ?? null,
    created_at: String(psy.created_at ?? ''),
    updated_at: String(psy.updated_at ?? ''),
  }
}

function mapObjectiveDetail(raw: unknown): SupportCenterTriageObjectiveDetail | null {
  const row = Array.isArray(raw) ? raw[0] : raw
  if (!row || typeof row !== 'object') return null
  const item = row as Record<string, unknown>
  if (!item.id) return null
  return {
    id: String(item.id),
    assistance_objective: (item.assistance_objective as string | null) ?? null,
    created_at: String(item.created_at ?? ''),
    updated_at: String(item.updated_at ?? ''),
  }
}

function mapJuridicalDetail(raw: unknown): SupportCenterTriageJuridicalDetail | null {
  const row = Array.isArray(raw) ? raw[0] : raw
  if (!row || typeof row !== 'object') return null
  const item = row as Record<string, unknown>
  if (!item.id) return null
  return {
    id: String(item.id),
    activation_reason: (item.activation_reason as string | null) ?? null,
    created_at: String(item.created_at ?? ''),
    updated_at: String(item.updated_at ?? ''),
  }
}

async function enrichTriageSpecificForms(
  rows: SupportCenterTriageListItem[]
): Promise<SupportCenterTriageListItem[]> {
  const idsByKey = new Map<SupportCenterFormKind, string[]>()
  for (const row of rows) {
    const key = row.form_key
    if (!isSupportCenterFormKind(key) || !row.id) continue
    const list = idsByKey.get(key) ?? []
    list.push(row.id)
    idsByKey.set(key, list)
  }

  const formKeys = [...idsByKey.keys()]
  const tableNamesByKey = await supportCenterFormsService.resolveTableNamesByKeys(formKeys)

  const detailByKeyAndId = new Map<string, Record<string, unknown>>()
  for (const [key, ids] of idsByKey) {
    const uniqueIds = [...new Set(ids)]
    if (uniqueIds.length === 0) continue
    const table = tableNamesByKey.get(key)?.triage_table_name
    if (!table) {
      throw new Error(`Formulário "${key}" sem tabela de triagem cadastrada.`)
    }
    const { data, error } = await supabase.from(table).select('*').in('id', uniqueIds)
    if (error) throw error
    for (const item of data ?? []) {
      const rowId = String((item as { id?: string }).id ?? '')
      if (!rowId) continue
      detailByKeyAndId.set(`${key}:${rowId}`, item as Record<string, unknown>)
    }
  }

  return rows.map((row) => {
    const key = row.form_key
    if (!isSupportCenterFormKind(key) || !row.id) {
      return {
        ...row,
        psychological: null,
        nutritional: null,
        fitness: null,
        juridical: null,
        social: null,
      }
    }
    const raw = detailByKeyAndId.get(`${key}:${row.id}`) ?? null
    return {
      ...row,
      psychological: key === 'psicologico' ? mapPsychological(raw) : null,
      nutritional: key === 'nutricional' ? mapObjectiveDetail(raw) : null,
      fitness: key === 'fitness' ? mapObjectiveDetail(raw) : null,
      juridical: key === 'juridico' ? mapJuridicalDetail(raw) : null,
      social: key === 'social' ? mapJuridicalDetail(raw) : null,
    }
  })
}

function mapSpecialization(
  row: unknown
): SupportCenterAppointment['specialization'] {
  if (!row || typeof row !== 'object') return null
  const spec = row as Record<string, unknown>
  const id = String(spec.id ?? '')
  if (!id) return null
  return {
    id,
    name: String(spec.name ?? ''),
  }
}

function mapAssistance(row: unknown): SupportCenterAppointment['assistance'] {
  if (!row || typeof row !== 'object') return null
  const assistance = row as Record<string, unknown>
  const id = String(assistance.id ?? '')
  if (!id) return null

  const specificFormRaw = assistance.specific_form
  const specificFormRow = Array.isArray(specificFormRaw) ? specificFormRaw[0] : specificFormRaw
  let specific_form: NonNullable<SupportCenterAppointment['assistance']>['specific_form'] = null
  if (specificFormRow && typeof specificFormRow === 'object') {
    const form = specificFormRow as Record<string, unknown>
    if (!form.deleted_at) {
      const form_key = String(form.form_key ?? '').trim()
      if (form_key) {
        specific_form = {
          form_key,
          name: String(form.name ?? '').trim(),
          table_name: String(form.table_name ?? '').trim(),
          triage_table_name: String(form.triage_table_name ?? '').trim(),
        }
      }
    }
  }

  const specific_form_key =
    specific_form?.form_key ||
    (typeof assistance.specific_form_key === 'string'
      ? assistance.specific_form_key.trim()
      : '') ||
    null

  return {
    id,
    name: String(assistance.name ?? ''),
    code: String(assistance.code ?? ''),
    form_kind: resolveAssistanceFormKind(specific_form_key),
    specific_form_key: specific_form_key || null,
    specific_form,
  }
}

function mapCancelReason(row: unknown): SupportCenterAppointment['cancel_reason'] {
  if (!row || typeof row !== 'object') return null
  const reason = Array.isArray(row) ? row[0] : row
  if (!reason || typeof reason !== 'object') return null
  const item = reason as Record<string, unknown>
  const id = String(item.id ?? '')
  if (!id) return null
  return {
    id,
    name: String(item.name ?? ''),
    description: String(item.description ?? ''),
  }
}

function mapAppointment(row: Record<string, unknown> | null): SupportCenterAppointment | null {
  if (!row) return null
  const phoneContactId = (row.phone_contact_id as string | null) ?? null
  const emailContactIds = asStringArray(row.email_contact_ids)
  const phoneContactRaw = row.phone_contact
  const phoneContact = Array.isArray(phoneContactRaw) ? phoneContactRaw[0] : phoneContactRaw
  const phoneFromJoin =
    phoneContact && typeof phoneContact === 'object'
      ? String((phoneContact as { value?: string }).value ?? '').trim() || null
      : null

  return {
    ...(row as unknown as SupportCenterAppointment),
    specialization_id: String(row.specialty_id ?? row.professional_id ?? row.specialization_id ?? ''),
    professional_user_id: (row.professional_user_id as string | null) ?? null,
    internal_emails: asStringArray(row.internal_emails),
    phone_contact_id: phoneContactId,
    email_contact_ids: emailContactIds,
    client_emails: [],
    call_link: (row.call_link as string | null) ?? null,
    call_link_short: (row.call_link_short as string | null) ?? null,
    patient_phone: phoneFromJoin,
    relationship_to_holder: (row.relationship_to_holder as string | null) ?? null,
    confirmed_at: (row.confirmed_at as string | null) ?? null,
    attendance_started_at: (row.attendance_started_at as string | null) ?? null,
    attendance_completed_at: (row.attendance_completed_at as string | null) ?? null,
    assistance_id: (row.assistance_id as string | null) ?? null,
    cancel_reason_id: (row.cancel_reason_id as string | null) ?? null,
    cancellation_channel:
      (row.cancellation_channel as SupportCenterAppointment['cancellation_channel']) ?? null,
    cancel_reason: mapCancelReason(row.cancel_reason),
    specialization: mapSpecialization(row.specialization),
    assistance: mapAssistance(row.assistance),
    professional: mapProfessional(row.professional),
    holder_people: mapPeople(row.holder_people),
    patient_people: mapPeople(row.patient_people),
  }
}

async function enrichTriageAppointmentsContacts(
  rows: SupportCenterTriageListItem[]
): Promise<SupportCenterTriageListItem[]> {
  const ids: string[] = []
  for (const row of rows) {
    const appointment = row.appointment
    if (!appointment) continue
    if (appointment.phone_contact_id) ids.push(appointment.phone_contact_id)
    for (const emailId of appointment.email_contact_ids ?? []) {
      if (emailId) ids.push(emailId)
    }
  }
  const contactsById = await peopleContactsService.getByIds(ids)

  return rows.map((row) => {
    const appointment = row.appointment
    if (!appointment) return row
    const phone =
      (appointment.phone_contact_id
        ? contactsById.get(appointment.phone_contact_id)?.value?.trim() || null
        : null) ?? appointment.patient_phone
    const emails = (appointment.email_contact_ids ?? [])
      .map((id) => contactsById.get(id)?.value?.trim() || '')
      .filter(Boolean)
    return {
      ...row,
      appointment: {
        ...appointment,
        patient_phone: phone,
        client_emails: emails,
      },
    }
  })
}

const APPOINTMENT_CASE_SELECT = `
  id,
  call_link,
  call_link_short,
  holder_cpf,
  patient_cpf,
  relationship_to_holder,
  internal_emails,
  phone_contact_id,
  email_contact_ids,
  specialty_id,
  assistance_id,
  channel,
  professional_user_id,
  scheduled_at,
  confirmed_at,
  attendance_started_at,
  attendance_completed_at,
  status,
  created_at,
  updated_at,
  deleted_at,
  deleted_by,
  cancel_reason_id,
  cancellation_channel,
  specialization:support_center_specialties!specialty_id(id, name),
  assistance:support_center_assistances!assistance_id(
    id, name, code, specific_form_key,
    specific_form:support_center_forms!specific_form_key(form_key, name, table_name, triage_table_name, deleted_at)
  ),
  cancel_reason:support_center_cancel_reasons!cancel_reason_id(id, name, description),
  professional:profiles!professional_user_id(id, name),
  phone_contact:people_contacts!phone_contact_id(id, value, contact_type),
  holder_people:people!holder_cpf(id, name, tax_id_number, captured_birth_date),
  patient_people:people!patient_cpf(id, name, tax_id_number, captured_birth_date)
`

const TRIAGE_CASE_SELECT = `
        *,
        cancel_reason:support_center_cancel_reasons!cancel_reason_id(id, name, description),
        intern:profiles!intern_id(id, name),
        triaged_by_user:profiles!triaged_by(id, name),
        referral_origin:support_center_referral_origins!referral_origin_id(id, name, requires_notes),
        triage_demand_types:support_center_triage_demand_types(
          notes,
          demand_type:support_center_demand_types!demand_type_id(id, name, requires_notes)
        )
`

function isAppointmentVisiblyActive(row: {
  status?: unknown
  deleted_at?: unknown
}): boolean {
  const status = String(row.status ?? '')
  if (status === 'cancelado') return false
  if (
    row.deleted_at &&
    (status === 'marcado' || status === 'reagendado')
  ) {
    return false
  }
  return true
}

function mapListItem(
  row: Record<string, unknown>,
  appointmentRow: Record<string, unknown> | null = null
): SupportCenterTriageListItem {
  const appointment = mapAppointment(
    appointmentRow ??
      (row.appointment && typeof row.appointment === 'object'
        ? (Array.isArray(row.appointment)
            ? (row.appointment[0] as Record<string, unknown> | undefined) ?? null
            : (row.appointment as Record<string, unknown>))
        : null)
  )

  const referralRaw = row.referral_origin
  const referral =
    referralRaw && typeof referralRaw === 'object' && !Array.isArray(referralRaw)
      ? {
          id: String((referralRaw as Record<string, unknown>).id),
          name: String((referralRaw as Record<string, unknown>).name ?? ''),
          requires_notes: Boolean((referralRaw as Record<string, unknown>).requires_notes),
        }
      : null

  const triageId = String(row.id ?? '').trim() || null

  return {
    ...(row as unknown as SupportCenterTriage),
    cancel_reason_id: (row.cancel_reason_id as string | null) ?? null,
    cancel_reason: mapCancelReason(row.cancel_reason),
    form_key: typeof row.form_key === 'string' ? row.form_key.trim() || null : null,
    triage_date: (row.triage_date as string | null) ?? null,
    intern_id: (row.intern_id as string | null) ?? null,
    emergency_contact_phone: (row.emergency_contact_phone as string | null) ?? null,
    emergency_contact_relation: (row.emergency_contact_relation as string | null) ?? null,
    referral_origin_id: (row.referral_origin_id as string | null) ?? null,
    referral_origin_other: (row.referral_origin_other as string | null) ?? null,
    main_reason: (row.main_reason as string | null) ?? null,
    availability: (row.availability as string | null) ?? null,
    triaged_by: (row.triaged_by as string | null) ?? null,
    intern: mapIntern(row.intern),
    triaged_by_user: mapIntern(row.triaged_by_user),
    referral_origin: referral,
    demand_types: mapDemandTypes(row.triage_demand_types),
    psychological: null,
    nutritional: null,
    fitness: null,
    juridical: null,
    social: null,
    appointment: appointment
      ? {
          ...appointment,
          triage_id: triageId,
        }
      : null,
  }
}

async function attachCaseAppointmentsToTriageRows(
  rows: Record<string, unknown>[]
): Promise<SupportCenterTriageListItem[]> {
  const triageIds = rows.map((row) => String(row.id ?? '').trim()).filter(Boolean)
  const appointmentByTriage = await resolveAppointmentsByTriageIds(
    triageIds,
    APPOINTMENT_CASE_SELECT
  )
  return rows.map((row) => {
    const triageId = String(row.id ?? '').trim()
    return mapListItem(row, appointmentByTriage.get(triageId) ?? null)
  })
}

function formToGeneralColumns(form: SupportCenterTriageCompleteInput['form']) {
  return {
    triage_date: form.triage_date || null,
    intern_id: form.intern_id || null,
    emergency_contact_phone: form.emergency_contact_phone.trim() || null,
    emergency_contact_relation: form.emergency_contact_relation.trim() || null,
    referral_origin_id: form.referral_origin_id || null,
    referral_origin_other: form.referral_origin_other.trim() || null,
    main_reason: form.main_reason.trim() || null,
    availability: form.availability.trim() || null,
  }
}

async function assertPendingTriage(triageId: string): Promise<void> {
  const { data, error } = await supabase
    .from('support_center_triages')
    .select('id, status')
    .eq('id', triageId)
    .is('deleted_at', null)
    .maybeSingle()

  if (error) throw error
  if (!data) throw new Error('Triagem não encontrada.')
  if (data.status !== 'pendente') {
    throw new Error('Esta triagem não está mais pendente.')
  }
}

/**
 * Valida triagem pendente e, se houver scheduled_at de contexto, a janela de horário.
 * Não grava nao_realizado na triagem compartilhada (um horário passado não fecha o caso).
 */
async function assertPendingTriageWithinWindow(
  triageId: string,
  scheduledAt?: string | null
): Promise<void> {
  const { pastToleranceMinutes } = await supportCenterParametersService.getSchedulingLimits()
  await assertPendingTriage(triageId)

  const when = scheduledAt?.trim() || null
  if (when && isAppointmentPastForAttendance(when, pastToleranceMinutes)) {
    throw new Error(triagePastMessage(pastToleranceMinutes))
  }
}

async function finalizeTriageWithAppointmentCancel(input: {
  id: string
  /** Agendamento de contexto a cancelar (só este; não todos do caso). */
  appointment_id: string
  cancel_reason_id: string
  actor_id: string
  cancellation_channel: 'whatsapp' | 'telefone' | 'videochamada'
  status: 'reprovado' | 'cancelado'
  reasonEmptyMessage: string
  apiFailMessage: string
}): Promise<void> {
  const cancelReasonId = input.cancel_reason_id.trim()
  if (!cancelReasonId) {
    throw new Error(input.reasonEmptyMessage)
  }
  if (
    input.cancellation_channel !== 'whatsapp' &&
    input.cancellation_channel !== 'telefone' &&
    input.cancellation_channel !== 'videochamada'
  ) {
    throw new Error('Selecione o canal do cancelamento (Telefone, WhatsApp ou Videochamada).')
  }

  const appointment = await assertAppointmentBelongsToTriageCase(
    input.id,
    input.appointment_id
  )

  // Cancelar triagem: só exige pendente.
  // Reprovar: mantém a janela do agendamento de contexto.
  if (input.status === 'cancelado') {
    await assertPendingTriage(input.id)
  } else {
    await assertPendingTriageWithinWindow(input.id, appointment.scheduled_at)
  }

  // Cancela só o agendamento de contexto ANTES de alterar a triagem.
  if (
    appointment.status === 'marcado' ||
    appointment.status === 'reagendado'
  ) {
    try {
      await supportCenterAppointmentsService.cancel(
        appointment.id,
        cancelReasonId,
        input.actor_id,
        input.cancellation_channel
      )
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error)
      if (/failed to fetch|networkerror|load failed/i.test(raw)) {
        throw new Error(input.apiFailMessage)
      }
      throw error
    }
  }

  const triageReasonPayload = {
    cancel_reason_id: cancelReasonId,
    triaged_at: nowTimestampSaoPaulo(),
    triaged_by: input.actor_id,
  }

  if (input.status === 'reprovado') {
    const { error } = await supabase
      .from('support_center_triages')
      .update({
        status: 'reprovado' satisfies SupportCenterTriageStatus,
        ...triageReasonPayload,
      })
      .eq('id', input.id)
      .in('status', ['pendente', 'cancelado'])
      .is('deleted_at', null)

    if (error) throw error
    return
  }

  const { error } = await supabase
    .from('support_center_triages')
    .update({
      status: 'cancelado' satisfies SupportCenterTriageStatus,
      ...triageReasonPayload,
    })
    .eq('id', input.id)
    .in('status', ['pendente', 'cancelado'])
    .is('deleted_at', null)

  if (error) throw error
}

async function upsertDetailByFormKind(
  formKind: SupportCenterFormKind,
  form: SupportCenterTriageCompleteInput['form'],
  triageId: string
): Promise<void> {
  const table = await supportCenterFormsService.getTriageTableName(formKind)

  if (formKind === 'psicologico') {
    const psy = form.psychological
    const payload = {
      suffering_intensity: psy.suffering_intensity || null,
      demand_nature: psy.demand_nature || null,
      suicidal_ideation: psy.suicidal_ideation || null,
      self_harm: psy.self_harm || null,
      aggression_risk: psy.aggression_risk || null,
      domestic_violence: psy.domestic_violence || null,
      observations: psy.observations.trim() || null,
    }
    await upsertTriageSpecificRow(table, triageId, payload)
    return
  }

  if (formKind === 'nutricional' || formKind === 'fitness') {
    const objective =
      formKind === 'nutricional'
        ? form.nutritional.assistance_objective
        : form.fitness.assistance_objective
    await upsertTriageSpecificRow(table, triageId, {
      assistance_objective: objective.trim() || null,
    })
    return
  }

  if (formKind === 'juridico' || formKind === 'social') {
    const reason =
      formKind === 'juridico'
        ? form.juridical.activation_reason
        : form.social.activation_reason
    await upsertTriageSpecificRow(table, triageId, {
      activation_reason: reason.trim() || null,
    })
    return
  }

  throw new Error('Tipo de formulário de triagem não suportado.')
}

async function upsertTriageSpecificRow(
  table: string,
  triageId: string,
  fields: Record<string, string | null>
): Promise<void> {
  const { data: existing, error: existingError } = await supabase
    .from(table)
    .select('id')
    .eq('id', triageId)
    .maybeSingle()
  if (existingError) throw existingError

  if (existing?.id) {
    const { error } = await supabase.from(table).update(fields).eq('id', triageId)
    if (error) throw error
    return
  }

  const { error } = await supabase.from(table).insert({ id: triageId, ...fields })
  if (error) throw error
}

/**
 * Remove o detalhe específico da triagem na tabela indicada (ex.: troca de formulário).
 */
async function deleteTriageSpecificDetail(
  formKey: SupportCenterFormKind,
  triageId: string
): Promise<void> {
  const table = await supportCenterFormsService.getTriageTableName(formKey)
  const { error } = await supabase.from(table).delete().eq('id', triageId)
  if (error) throw error
}

async function linkTriageFormKey(
  triageId: string,
  formKey: SupportCenterFormKind
): Promise<void> {
  const { error } = await supabase
    .from('support_center_triages')
    .update({ form_key: formKey })
    .eq('id', triageId)
    .eq('status', 'pendente')
    .is('deleted_at', null)
  if (error) throw error
}

function cpfLookupVariants(cpf: string): string[] {
  const digits = onlyDigits(cpf)
  if (digits.length !== 11) return digits ? [digits] : []
  const variants = new Set([digits])
  const trimmed = digits.replace(/^0+/, '')
  if (trimmed && trimmed !== digits) variants.add(trimmed)
  return [...variants]
}

export const supportCenterTriageService = {
  async findPersonByCpf(cpf: string): Promise<SupportCenterPeopleRef | null> {
    const variants = cpfLookupVariants(cpf)
    if (variants.length === 0) return null

    const { data, error } = await supabase
      .from('people')
      .select('id, name, tax_id_number, captured_birth_date')
      .in('tax_id_number', variants)
      .limit(1)

    if (error) throw error

    const row = data?.[0]
    if (!row) return null

    return {
      id: Number(row.id),
      name: (row.name as string | null) ?? null,
      tax_id_number: (row.tax_id_number as string | null) ?? null,
      captured_birth_date: (row.captured_birth_date as string | null) ?? null,
    }
  },

  /** Perfis que já concluíram/reprovaram ao menos uma triagem (distinct triaged_by). */
  async listTriagedByProfiles(): Promise<Array<{ id: string; name: string }>> {
    const { data, error } = await supabase
      .from('support_center_triages')
      .select(
        `
        triaged_by,
        triaged_by_user:profiles!triaged_by(id, name, is_active)
      `
      )
      .not('triaged_by', 'is', null)
      .is('deleted_at', null)

    if (error) throw error

    const byId = new Map<string, string>()
    for (const row of data ?? []) {
      const profileRaw = row.triaged_by_user
      const profile = Array.isArray(profileRaw) ? profileRaw[0] : profileRaw
      if (!profile || typeof profile !== 'object') continue
      const p = profile as { id?: string; name?: string | null; is_active?: boolean }
      if (!p.id || p.is_active === false) continue
      if (!byId.has(p.id)) {
        byId.set(p.id, (p.name ?? '').trim() || p.id)
      }
    }

    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
  },

  async listCases(filters: SupportCenterTriageFilters = {}): Promise<SupportCenterTriageListItem[]> {
    let query = supabase
      .from('support_center_triages')
      .select(TRIAGE_CASE_SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (filters.statuses) {
      const unique = Array.from(new Set(filters.statuses))
      if (unique.length === 0) {
        query = query.eq('status', 'pendente')
      } else {
        const isAll = (Object.keys(TRIAGE_STATUS_LABELS) as SupportCenterTriageStatus[]).every(
          (status) => unique.includes(status)
        )
        if (!isAll) {
          query = query.in('status', unique)
        }
      }
    } else {
      const status = filters.status ?? 'pendente'
      if (status !== 'all') {
        query = query.eq('status', status)
      }
    }

    const { data, error } = await query
    if (error) throw error

    let rows = await enrichTriageAppointmentsContacts(
      await attachCaseAppointmentsToTriageRows((data ?? []) as Record<string, unknown>[])
    )
    rows = await enrichTriageSpecificForms(rows)

    if (filters.cpf?.trim()) {
      const cpf = onlyDigits(filters.cpf)
      rows = rows.filter((row) => {
        return (
          onlyDigits(row.appointment?.patient_cpf ?? '').includes(cpf) ||
          onlyDigits(row.appointment?.holder_cpf ?? '').includes(cpf) ||
          onlyDigits(row.appointment?.holder_people?.tax_id_number ?? '').includes(cpf) ||
          onlyDigits(row.appointment?.patient_people?.tax_id_number ?? '').includes(cpf)
        )
      })
    }

    if (filters.specialization_ids && filters.specialization_ids.length > 0) {
      const allowed = new Set(filters.specialization_ids)
      rows = rows.filter((row) => {
        const appt = row.appointment
        if (!appt?.specialization_id || !allowed.has(appt.specialization_id)) return false
        // Reprovado/cancelado cancela o agendamento; status finais precisam continuar listáveis.
        if (row.status !== 'pendente') return true
        return isAppointmentVisiblyActive(appt)
      })
    }
    // specialization_ids vazio / omitido = sem filtro por profissional

    if (filters.attendant_email?.trim()) {
      const email = normalizeEmail(filters.attendant_email)
      rows = rows.filter((row) => {
        const appt = row.appointment
        if (!appt) return false
        const emails = (appt.internal_emails ?? []).map(normalizeEmail)
        if (!emails.includes(email)) return false
        // Mesma regra do filtro por profissional: pendente só com agenda ativa.
        if (row.status !== 'pendente') return true
        return isAppointmentVisiblyActive(appt)
      })
    }

    if (filters.specialization_filter_ids && filters.specialization_filter_ids.length > 0) {
      const allowed = new Set(filters.specialization_filter_ids)
      rows = rows.filter((row) => {
        const id = row.appointment?.specialization_id
        return Boolean(id && allowed.has(id))
      })
    } else if (filters.specialization_id) {
      rows = rows.filter(
        (row) => row.appointment?.specialization_id === filters.specialization_id
      )
    }

    if (filters.professional_user_ids && filters.professional_user_ids.length > 0) {
      const allowed = new Set(filters.professional_user_ids)
      rows = rows.filter((row) => {
        const appt = row.appointment
        if (!appt) return false
        const id = appt.professional_user_id || appt.professional?.id || ''
        return allowed.has(id)
      })
    } else if (filters.professional_user_id) {
      const professionalId = filters.professional_user_id
      rows = rows.filter((row) => {
        const appt = row.appointment
        if (!appt) return false
        const id = appt.professional_user_id || appt.professional?.id || ''
        return id === professionalId
      })
    }

    if (filters.triaged_by_ids && filters.triaged_by_ids.length > 0) {
      const allowed = new Set(filters.triaged_by_ids)
      rows = rows.filter((row) => Boolean(row.triaged_by && allowed.has(row.triaged_by)))
    } else if (filters.triaged_by) {
      const triagedBy = filters.triaged_by
      rows = rows.filter((row) => row.triaged_by === triagedBy)
    }

    if (filters.date_from) {
      const from = timestampDayStart(filters.date_from)
      rows = rows.filter(
        (row) => row.appointment?.scheduled_at && row.appointment.scheduled_at >= from
      )
    }

    if (filters.date_to) {
      const to = timestampDayEnd(filters.date_to)
      rows = rows.filter(
        (row) => row.appointment?.scheduled_at && row.appointment.scheduled_at <= to
      )
    }

    if (filters.phone?.trim()) {
      const phone = onlyDigits(filters.phone)
      let taxIdsFromContacts = new Set<string>()
      try {
        const ids = await peopleContactsService.findTaxIdsMatchingPhone(phone)
        taxIdsFromContacts = new Set(ids.map(onlyDigits))
      } catch {
        taxIdsFromContacts = new Set()
      }
      rows = rows.filter((row) => {
        const appointmentPhone = onlyDigits(row.appointment?.patient_phone ?? '')
        if (appointmentPhone.includes(phone)) return true
        const holder = onlyDigits(row.appointment?.holder_cpf ?? '')
        const patient = onlyDigits(row.appointment?.patient_cpf ?? '')
        return taxIdsFromContacts.has(holder) || taxIdsFromContacts.has(patient)
      })
    }

    rows.sort((a, b) => {
      const aHas = Boolean(a.appointment?.scheduled_at)
      const bHas = Boolean(b.appointment?.scheduled_at)
      if (aHas && !bHas) return -1
      if (!aHas && bHas) return 1
      const aDate = a.appointment?.scheduled_at ?? a.created_at
      const bDate = b.appointment?.scheduled_at ?? b.created_at
      // Mais próxima → mais distante
      return aDate.localeCompare(bDate)
    })

    return rows
  },

  /**
   * @param preferredAppointmentId quando aberto a partir do calendário, fixa o agendamento de contexto.
   */
  async getCaseById(
    triageId: string,
    preferredAppointmentId?: string | null
  ): Promise<SupportCenterTriageListItem | null> {
    if (!triageId) return null

    const { data, error } = await supabase
      .from('support_center_triages')
      .select(TRIAGE_CASE_SELECT)
      .eq('id', triageId)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null

    const [mapped] = await attachCaseAppointmentsToTriageRows([
      data as Record<string, unknown>,
    ])
    let item = mapped
    const preferredId = preferredAppointmentId?.trim()
    if (preferredId) {
      const { data: preferred, error: preferredError } = await supabase
        .from('support_center_appointments')
        .select(APPOINTMENT_CASE_SELECT)
        .eq('id', preferredId)
        .maybeSingle()
      if (preferredError) throw preferredError
      if (preferred) {
        await assertAppointmentBelongsToTriageCase(triageId, preferredId)
        item = mapListItem(data as Record<string, unknown>, preferred as Record<string, unknown>)
      }
    }

    const [enriched] = await enrichTriageSpecificForms(
      await enrichTriageAppointmentsContacts([item])
    )
    return enriched ?? null
  },

  async saveTriageForm(input: SupportCenterTriageSaveFormInput): Promise<void> {
    if (!input.form.intern_id) {
      throw new Error('Selecione o responsável pela triagem.')
    }

    let scheduledAt: string | null = null
    if (input.appointment_id?.trim()) {
      const appointment = await assertAppointmentBelongsToTriageCase(
        input.id,
        input.appointment_id
      )
      scheduledAt = appointment.scheduled_at
    } else {
      const byTriage = await resolveAppointmentsByTriageIds(
        [input.id],
        'id, status, deleted_at, scheduled_at, patient_cpf, assistance_id'
      )
      const open = byTriage.get(input.id)
      scheduledAt = open ? String(open.scheduled_at ?? '') || null : null
    }
    await assertPendingTriageWithinWindow(input.id, scheduledAt)

    const demandSelections = normalizeDemandSelections(input.form.demand_selections)

    const { data: current, error: currentError } = await supabase
      .from('support_center_triages')
      .select('form_key')
      .eq('id', input.id)
      .eq('status', 'pendente')
      .is('deleted_at', null)
      .maybeSingle()
    if (currentError) throw currentError

    const previousFormKey = isSupportCenterFormKind(current?.form_key) ? current.form_key : null

    const { error } = await supabase
      .from('support_center_triages')
      .update(formToGeneralColumns(input.form))
      .eq('id', input.id)
      .eq('status', 'pendente')
      .is('deleted_at', null)

    if (error) throw error

    const { error: deleteError } = await supabase
      .from('support_center_triage_demand_types')
      .delete()
      .eq('triage_id', input.id)

    if (deleteError) throw deleteError

    if (demandSelections.length > 0) {
      const { error: insertError } = await supabase
        .from('support_center_triage_demand_types')
        .insert(
          demandSelections.map(({ demand_type_id, notes }) => ({
            triage_id: input.id,
            demand_type_id,
            notes: notes.trim() || null,
          }))
        )
      if (insertError) throw insertError
    }

    await upsertDetailByFormKind(input.form_kind, input.form, input.id)
    await linkTriageFormKey(input.id, input.form_kind)

    // Troca de formulário: só remove o detalhe anterior depois que o novo persistiu.
    if (previousFormKey && previousFormKey !== input.form_kind) {
      await deleteTriageSpecificDetail(previousFormKey, input.id)
    }
  },

  async completeTriage(input: SupportCenterTriageCompleteInput): Promise<void> {
    if (!input.form.referral_origin_id) {
      throw new Error('Selecione a origem do encaminhamento.')
    }
    if (isReducedTriageForm(input.form_kind)) {
      if (!reducedTriageText(input.form, input.form_kind).trim()) {
        throw new Error(reducedTriageRequiredMessage(input.form_kind))
      }
      if (normalizeDemandSelections(input.form.demand_selections).length === 0) {
        throw new Error('Selecione ao menos um tipo de demanda.')
      }
    }

    await this.saveTriageForm(input)

    const { error } = await supabase
      .from('support_center_triages')
      .update({
        status: 'concluida' satisfies SupportCenterTriageStatus,
        cancel_reason_id: null,
        triaged_at: nowTimestampSaoPaulo(),
        triaged_by: input.actor_id,
      })
      .eq('id', input.id)
      .eq('status', 'pendente')
      .is('deleted_at', null)

    if (error) throw error
  },

  async rejectTriage(input: SupportCenterTriageRejectInput): Promise<void> {
    await finalizeTriageWithAppointmentCancel({
      ...input,
      status: 'reprovado',
      reasonEmptyMessage: 'Informe o motivo da reprovação.',
      apiFailMessage:
        'Não foi possível cancelar o agendamento na IA-API. A triagem não foi reprovada. Verifique se a API está no ar e tente de novo.',
    })
  },

  async cancelTriage(input: SupportCenterTriageCancelInput): Promise<void> {
    await finalizeTriageWithAppointmentCancel({
      ...input,
      status: 'cancelado',
      reasonEmptyMessage: 'Informe o motivo do cancelamento.',
      apiFailMessage:
        'Não foi possível cancelar o agendamento na IA-API. A triagem não foi cancelada. Verifique se a API está no ar e tente de novo.',
    })
  },
}
